import { computed, Injectable, signal } from '@angular/core';
import { AuthenticatedUser, AuthStatus } from './auth.types';

@Injectable({
  providedIn: 'root',
})
export class AuthStore {
  private readonly userSignal = signal<AuthenticatedUser | null>(null);
  private readonly loadingSignal = signal(false);
  private readonly initializedSignal = signal(false);

  readonly user = this.userSignal.asReadonly();
  readonly displayName = computed(() => {
    const user = this.userSignal();
    if (!user) return null;
    const names = user.nome.trim().split(/\s+/);
    if (names.length > 1) {
      return `${names[0]} ${names[names.length - 1]}`;
    }
    return names[0];
  });
  readonly loading = this.loadingSignal.asReadonly();
  readonly initialized = this.initializedSignal.asReadonly();
  readonly status = computed<AuthStatus>(() =>
    this.userSignal() ? 'authenticated' : 'unauthenticated',
  );
  readonly isAuthenticated = computed(() => this.status() === 'authenticated');
  readonly isSuperAdmin = computed(() => {
    const user = this.userSignal();
    return user?.superAdmin === true;
  });
  readonly hasRole = computed(
    () =>
      this.isSuperAdmin() ||
      !!this.userSignal()?.role ||
      (this.userSignal()?.acessos?.length ?? 0) > 0,
  );
  readonly effectiveRole = computed(() => {
    const user = this.userSignal();
    if (!user) return null;
    if (user.superAdmin) return 'SUPER_ADMIN';
    return user.role || user.acessos?.[0]?.role || null;
  });
  readonly canSelectModule = computed(() => {
    if (this.isSuperAdmin()) return true;
    const role = this.effectiveRole()?.toUpperCase();
    return role === 'ADMIN' || role === 'ADMINISTRADOR';
  });

  setLoading(isLoading: boolean): void {
    this.loadingSignal.set(isLoading);
  }

  setAuthenticatedUser(user: AuthenticatedUser): void {
    this.userSignal.set(user);
    this.initializedSignal.set(true);
  }

  clearSession(): void {
    this.userSignal.set(null);
    this.initializedSignal.set(true);
  }

  finishSessionRestore(user: AuthenticatedUser | null): void {
    this.userSignal.set(user);
    this.initializedSignal.set(true);
  }
}
